import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import Modal from "react-bootstrap/Modal";
import { Form, Row, Col } from "react-bootstrap";
import { updateDb } from "../services/updateDb";
import { useAppContext } from "../AppContext";
import { useTranslation } from "react-i18next";
import useSoaCheck from "../common/useSoaCheck";
import { getCategoryValue, getInputScoreColorClass, getRiskColor, shouldSuggestAcceptTreatment } from "../common/helpers";
import moment from "moment";

// Replaces the previously auto-generated change lines in the reason text with the new ones,
// leaving any text the user typed (or that was prefilled) untouched.
const mergeAutoReasonLines = (text, previousAutoLines, newAutoLines) => {
  const remaining = [...previousAutoLines];
  const kept = [];
  let insertAt = -1;

  (text || "").split("\n").forEach((line) => {
    const matchIndex = remaining.indexOf(line);
    if (matchIndex !== -1) {
      remaining.splice(matchIndex, 1);
      if (insertAt === -1) insertAt = kept.length;
    } else {
      kept.push(line);
    }
  });

  if (insertAt === -1) {
    insertAt = kept.length;
    while (insertAt > 0 && kept[insertAt - 1].trim() === "") insertAt--;
  }

  kept.splice(insertAt, 0, ...newAutoLines);
  return kept.join("\n");
};

function UpdateRiskScore({ riskObj, show, setRefreshData, updateReason }) {
  const { t } = useTranslation();
  const [initialState, setInitialState] = useState({});
  const [currentReason, setCurrentReason] = useState("");
  const autoReasonLinesRef = useRef([]);
  const reasonTextareaRef = useRef(null);
  const [measureStatuses, setMeasureStatuses] = useState([]);
  const [measureTexts, setMeasureTexts] = useState([]);
  const [riskDescription, setRiskDescription] = useState("");
  const [riskTreatment, setRiskTreatment] = useState("");
  const [treatmentHow, setTreatmentHow] = useState("");
  const [tempAcceptTreatment, setTempAcceptTreatment] = useState(false);
  const [acceptTreatmentDate, setAcceptTreatmentDate] = useState("");
  const [liveChance, setLiveChance] = useState(null);
  const [liveImpact, setLiveImpact] = useState(null);
  const { currentOrg, showAlertMessage, closeModal } = useAppContext();
  const { checkSoaAfterMeasureUpdate } = useSoaCheck();

  useEffect(() => {
    if (show) {
      const initState = {
        measures: riskObj.measures.map((measure) => measure.status),
        chance: riskObj["actual-chance"],
        impact: riskObj["actual-impact"],
        treatment: riskObj.treatment || ""
      };
      setInitialState(initState);
      setCurrentReason(updateReason || "");
      autoReasonLinesRef.current = [];
      setMeasureStatuses(riskObj.measures.map((measure) => measure.status || ""));
      setMeasureTexts(riskObj.measures.map((measure) => measure.measure || ""));
      setRiskDescription(riskObj.description || "");
      setRiskTreatment(riskObj.treatment || "");
      setTreatmentHow(riskObj.treatmentHow || "");
      setTempAcceptTreatment(riskObj.tempAcceptTreatment || false);
      setAcceptTreatmentDate(riskObj.acceptTreatmentDate || "");
      setLiveChance(riskObj["actual-chance"] || null);
      setLiveImpact(riskObj["actual-impact"] || null);
    }
  }, [show, riskObj, updateReason]);

  // Grow the reason textarea with its content so the text stays readable
  const resizeReasonTextarea = useCallback(() => {
    const el = reasonTextareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  }, []);

  useLayoutEffect(() => {
    resizeReasonTextarea();
  }, [currentReason, show, resizeReasonTextarea]);

  const handleModalClose = async (updatedRiskObj) => {
    if (updatedRiskObj) {
      const res = await updateDb("/api/risks/update-score/" + updatedRiskObj.id, updatedRiskObj);

      if (res.success) {
        setRefreshData(new Date().getTime());
        showAlertMessage('warning', t('risk') + " score " + t('updated'));

        // Check if any SoA controls should be marked as implemented
        const affectedCategories = [...new Set(
          riskObj.measures.flatMap(m => (m.category || []).map(c => getCategoryValue(c)))
        )];
        if (affectedCategories.length > 0) {
          checkSoaAfterMeasureUpdate(affectedCategories);
        }
      } else {
        showAlertMessage('warning', res?.error);
      }
    }

    closeModal();
  };

  const handleMeasureChange = (e, index) => {
    const updatedMeasures = [...measureStatuses];
    updatedMeasures[index] = e.target.value;
    setMeasureStatuses(updatedMeasures);
    generateUpdateReason({ measures: updatedMeasures });
  };

  const handleMeasureTextChange = (e, index) => {
    const updatedTexts = [...measureTexts];
    updatedTexts[index] = e.target.value;
    setMeasureTexts(updatedTexts);
    generateUpdateReason({ texts: updatedTexts });
  };

  const handleDescriptionChange = (e) => {
    setRiskDescription(e.target.value);
    generateUpdateReason({ description: e.target.value });
  };

  const updateActualScore = () => {
    let scoreInput = document.querySelector('input[name="actual-score"]');
    let formData = new FormData(document.querySelector("form#updateRisk"));

    const chance = formData.get("actual-chance");
    const impact = formData.get("actual-impact");

    if (!chance || !impact) {
      scoreInput.value = "-";
      let classes = scoreInput.className.split(" ");
      classes = classes.filter((cls) => !cls.startsWith("input-score-color-"));
      classes.push("input-score-color-neutral");
      scoreInput.className = classes.join(" ");
      generateUpdateReason();
      return;
    }

    let score = chance * impact;

    scoreInput.value = score;
    let classes = scoreInput.className.split(" ");
    classes = classes.filter((cls) => !cls.startsWith("input-score-color-"));
    const newClass = getInputScoreColorClass(score, currentOrg.rangeBounds);
    classes.push(newClass);
    scoreInput.className = classes.join(" ");

    generateUpdateReason();
  };

  const generateUpdateReason = ({
    measures: updatedMeasures = measureStatuses,
    texts: updatedTexts = measureTexts,
    description: updatedDescription = riskDescription,
    treatment: riskTreatmentValue
  } = {}) => {
    const autoLines = [];
    const formData = new FormData(document.querySelector("form#updateRisk"));

    const currentChance = formData.get("actual-chance");
    const currentImpact = formData.get("actual-impact");

    // Compare risk description
    if ((riskObj.description || "").trim() !== (updatedDescription || "").trim()) {
      autoLines.push(`- Risk description updated.`);
    }

    // Compare measure descriptions
    riskObj.measures.forEach((measure, index) => {
      const oldText = (measure.measure || "").trim();
      const newText = (updatedTexts[index] ?? "").trim();
      if (oldText !== newText) {
        autoLines.push(`- Measure description: ${oldText || "-"} -> ${newText || "-"}.`);
      }
    });

    // Compare measures
    riskObj.measures.forEach((measure, index) => {
      const oldStatus = measure.status || "";
      const newStatus = updatedMeasures[index] || "";
      if (oldStatus !== newStatus && newStatus !== "") {
        autoLines.push(`- Measure update: ${(updatedTexts[index] ?? "").trim() || measure.measure} from ${oldStatus || "not set"} -> ${newStatus}.`);
      }
    });

    // Compare chance
    const parsedChance = currentChance ? parseInt(currentChance) : 0;
    if (parseInt(initialState.chance) !== parsedChance) {
      autoLines.push(`- Chance: From ${initialState.chance || "-"} -> ${currentChance || "-"}.`);
    }

    // Compare impact
    const parsedImpact = currentImpact ? parseInt(currentImpact) : 0;
    if (parseInt(initialState.impact) !== parsedImpact) {
      autoLines.push(`- Impact: From ${initialState.impact || "-"} -> ${currentImpact || "-"}.`);
    }

    // Compare risk treatment
    if (initialState.treatment !== (riskTreatmentValue || riskTreatment)) {
      autoLines.push(`- Treatment: From ${initialState.treatment || ""} -> ${riskTreatmentValue || riskTreatment}.`);
    }

    setCurrentReason(mergeAutoReasonLines(currentReason, autoReasonLinesRef.current, autoLines));
    autoReasonLinesRef.current = autoLines;
  };

  if (riskObj && riskObj.measures) {
    const suggestAccept = shouldSuggestAcceptTreatment(
      { ...riskObj, treatment: riskTreatment },
      { actualChance: liveChance, actualImpact: liveImpact }
    );

    return (
      <Modal show={show} onHide={closeModal} onEntered={resizeReasonTextarea} size="lg">
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title>{t('update_score')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="update-score-modal">
          <Form id="updateRisk">
            <Form.Group className="mb-3" controlId="riskDescription">
              <Form.Label><b>{t('risk_description')}:</b></Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="description"
                value={riskDescription}
                onChange={handleDescriptionChange}
                isInvalid={!riskDescription.trim()}
              />
              <Form.Control.Feedback type="invalid">
                {t('risk_description_required')}
              </Form.Control.Feedback>
            </Form.Group>
            <b>{t('measures')}:</b>
            {riskObj.measures.map((measure, index) => (
              <Row className="mb-3" key={"measure" + index}>
                <Col>
                  <Form.Control
                    type="text"
                    name={"measure-text-" + index}
                    aria-label={t('measure')}
                    value={measureTexts[index] ?? ""}
                    onChange={(e) => handleMeasureTextChange(e, index)}
                  />
                </Col>
                <Col>
                  <Form.Select
                    name={"measure-status-" + index}
                    onChange={(e) => handleMeasureChange(e, index)}
                    defaultValue={measure.status}
                  >
                    <option value="">{t('measure')} status</option>
                    <option value="todo">{t('todo')}</option>
                    <option value="in_progress">{t('in_progress')}</option>
                    <option value="on_hold">{t('on_hold')}</option>
                    <option value="implemented">{t('implemented')}</option>
                    <option value="dismissed">{t('dismissed')}</option>
                  </Form.Select>
                </Col>
              </Row>
            ))}

            <Row className="mb-3">
              <Form.Group as={Col} controlId="actualChance">
                <Form.Label>{t('chance')}</Form.Label>
                <Form.Select name="actual-chance" onChange={(e) => { setLiveChance(e.target.value ? parseInt(e.target.value, 10) : null); updateActualScore(); }} defaultValue={riskObj["actual-chance"] || ""}>
                  <option value="">-</option>
                  {[...Array(5)].map((x, i) => (
                    <option key={i} value={i + 1}>{i + 1}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group as={Col} controlId="actualImpact">
                <Form.Label>{t('impact')}</Form.Label>
                <Form.Select name="actual-impact" onChange={(e) => { setLiveImpact(e.target.value ? parseInt(e.target.value, 10) : null); updateActualScore(); }} defaultValue={riskObj["actual-impact"] || ""}>
                  <option value="">-</option>
                  {Array.from(Array(5).keys()).map((item, index) => (
                    <option key={index} value={index + 1}>{index + 1}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group as={Col} controlId="actualScore">
                <Form.Label>Score</Form.Label>
                <Form.Control
                  name="actual-score"
                  className={"input-score-color-" + (!riskObj["actual-chance"] || !riskObj["actual-impact"] ? "neutral" : getRiskColor(riskObj["actual-chance"] * riskObj["actual-impact"], currentOrg.rangeBounds))}
                  defaultValue={riskObj["actual-chance"] && riskObj["actual-impact"] ? riskObj["actual-chance"] * riskObj["actual-impact"] : "-"}
                  required
                  data-readonly
                />
              </Form.Group>
            </Row>

            <b>{t('risk_treatment')}:</b>
            {suggestAccept && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 16,
                padding: '12px 16px',
                margin: '8px 0 16px',
                borderRadius: 6,
                border: '1px solid var(--green-risk-score)',
                background: 'rgba(40, 167, 69, 0.08)',
                borderLeft: '4px solid var(--green-risk-score)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <i className="icon-idea" style={{ fontSize: 18, color: 'var(--green-risk-score)' }} />
                  <span style={{ fontSize: 13 }}>{t('treatment_suggestion_inline')}</span>
                </div>
                <div
                  className="white-border-btn"
                  style={{ flexShrink: 0 }}
                  onClick={() => {
                    setRiskTreatment('accept');
                    generateUpdateReason({ treatment: 'accept' });
                  }}
                >
                  <i className="icon-check" /> {t('treatment_suggestion_apply')}
                </div>
              </div>
            )}
            <Row className="mb-3">
              <Form.Group as={Col} controlId="treatment">
                <Form.Label>{t('treatment')}</Form.Label>
                <Form.Select
                  name="treatment"
                  value={riskTreatment}
                  onChange={(e) => {
                    setRiskTreatment(e.target.value);
                    generateUpdateReason({ treatment: e.target.value });
                  }}
                >
                  <option value="reduce">{t('reduce')}</option>
                  <option value="accept">{t('accept')}</option>
                  <option value="avoid">{t('avoid')}</option>
                  <option value="transfer">{t('transfer')}</option>
                </Form.Select>
              </Form.Group>

              {(riskTreatment === "avoid" || riskTreatment === "transfer") && (
                <Form.Group as={Col} controlId="treatmentHow">
                  <Form.Label>{t('how')}?</Form.Label>
                  <Form.Control
                    as="input"
                    name="treatmentHow"
                    value={treatmentHow}
                    onChange={(e) => setTreatmentHow(e.target.value)}
                    placeholder={riskTreatment === "avoid" ? t('how_avoid') : t('how_transfer')}
                  />
                </Form.Group>
              )}

              {riskTreatment === "accept" && (
                <Form.Group as={Col} controlId="tempAcceptTreatment">
                  <div onClick={(e) => e.stopPropagation()}>
                    <Form.Check
                      type="checkbox"
                      id="tempAcceptTreatment"
                      name="tempAcceptTreatment"
                      label={t('temporarily_accept') + "?"}
                      onChange={(e) => setTempAcceptTreatment(e.target.checked)}
                      checked={tempAcceptTreatment}
                      style={{ marginTop: "50px", width: 'fit-content' }}
                    />
                  </div>
                </Form.Group>
              )}

              {riskTreatment === "accept" && tempAcceptTreatment && (
                <Form.Group as={Col} controlId="acceptTreatmentDate">
                  <Form.Label>{t('until_when')}</Form.Label>
                  <Form.Control
                    type="date"
                    name="acceptTreatmentDate"
                    value={acceptTreatmentDate}
                    onChange={(e) => setAcceptTreatmentDate(e.target.value)}
                    placeholder={t('until_when_placeholder')}
                  />
                </Form.Group>
              )}
            </Row>

            <label>
              <b>{t('reason_for_updating')}:</b>
              <br />
              <textarea
                ref={reasonTextareaRef}
                rows="3"
                name="reason"
                style={{ maxHeight: "50vh", overflowY: "auto" }}
                value={currentReason}
                placeholder={t('reason_for_updating_placeholder')}
                onChange={(e) => setCurrentReason(e.target.value)}
              />
            </label>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <div className="white-border-btn cancel-btn" onClick={closeModal}>
            {t('cancel')}
          </div>

          <div style={{ flexGrow: 1 }} />

          <div
            className="white-border-btn submit-btn"
            onClick={() => {
              if (!riskDescription.trim()) {
                showAlertMessage('warning', t('risk_description_required'));
                return;
              }
              const chanceVal = document.querySelector('select[name="actual-chance"]').value;
              const impactVal = document.querySelector('select[name="actual-impact"]').value;
              handleModalClose({
                id: riskObj.id,
                "actual-chance": chanceVal ? parseInt(chanceVal, 10) : null,
                "actual-impact": impactVal ? parseInt(impactVal, 10) : null,
                measures: measureStatuses,
                measureDescriptions: measureTexts,
                description: riskDescription,
                reason: document.querySelector('textarea[name="reason"]').value,
                treatment: riskTreatment,
                treatmentHow: treatmentHow,
                tempAcceptTreatment: tempAcceptTreatment,
                acceptTreatmentDate: acceptTreatmentDate
              });
            }}
          >
            {t('update')}
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default UpdateRiskScore;
